import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  Plus,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  Sparkles,
  ArrowUpDown,
  Eye,
  RefreshCw,
  Users,
  Building2,
  X,
  Loader2,
  FolderKanban,
  Star,
  Diamond,
  Package,
  Trash2
} from 'lucide-react';
import { useApplicants, useBatchReviewApplicants, useExportApplicants, useDeleteApplicant } from '../hooks/useApplicants';
import { useDebounce } from '../hooks/useDebounce';
import { useClickOutside } from '../hooks/useClickOutside';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import { useToast } from '../contexts/ToastContext';
import { ApplicantsTableSkeleton } from './shared/LoadingSkeleton';
import { ErrorState } from './shared/ErrorState';
import { ConfirmDialog } from './shared';
import CreateApplicantModal from './CreateApplicantModal';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { IconButton } from './ui/icon-button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Pagination } from './ui/pagination';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { formatEnumLabel, shortRef, getInitials, getAvatarColor } from '../utils/formatters';
import { useTestMode } from '../contexts/TestModeContext';

const statusConfig = {
  approved: { label: 'Approved', variant: 'approved', icon: CheckCircle2 },
  rejected: { label: 'Rejected', variant: 'rejected', icon: XCircle },
  pending: { label: 'Pending', variant: 'pending', icon: Clock },
  in_progress: { label: 'In Progress', variant: 'pending', icon: Clock },
  review: { label: 'Review', variant: 'in-review', icon: Eye },
  init: { label: 'New', variant: 'init', icon: Clock },
};

// Screening flag config - using shorter labels for clarity (SAN, PEP, ADV)
const screeningConfig = {
  pep: { label: 'PEP', shortLabel: 'PEP', variant: 'flag-warning' },
  sanctions: { label: 'Sanctions', shortLabel: 'SAN', variant: 'flag-danger' },
  adverse: { label: 'Adverse Media', shortLabel: 'ADV', variant: 'flag-info' },
  adverse_media: { label: 'Adverse Media', shortLabel: 'ADV', variant: 'flag-info' },
  forgery: { label: 'Forgery', shortLabel: 'FORG', variant: 'flag-danger' },
  biometrics_failed: { label: 'Biometrics Failed', shortLabel: 'BIO', variant: 'flag-danger' },
  screening_hit: { label: 'Screening Hit', shortLabel: 'HIT', variant: 'flag-warning' },
  name_mismatch: { label: 'Name Mismatch', shortLabel: 'NAME', variant: 'flag-warning' },
  high_risk_country: { label: 'High Risk Country', shortLabel: 'HRC', variant: 'flag-warning' },
  data_mismatch: { label: 'Data Mismatch', shortLabel: 'DATA', variant: 'flag-warning' },
  vpn: { label: 'VPN', shortLabel: 'VPN', variant: 'flag-warning' },
  proxy: { label: 'Proxy', shortLabel: 'PROXY', variant: 'flag-warning' },
  tor: { label: 'Tor', shortLabel: 'TOR', variant: 'flag-danger' },
  bot: { label: 'Bot', shortLabel: 'BOT', variant: 'flag-danger' },
  monitoring_enabled: { label: 'Monitored', shortLabel: 'MON', variant: 'flag-info' },
};

// screeningConfig is the primary config; flagConfig alias removed (was unused)

// Priority config for VIP/Priority badges
const priorityConfig = {
  vip: { label: 'VIP', icon: Star, variant: 'vip' },
  priority: { label: 'Priority', icon: Diamond, variant: 'priority' },
  standard: { label: 'Standard', icon: null, variant: 'muted' },
};

const getRiskColor = (bucket) => {
  switch (bucket) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'danger';
    default: return 'muted';
  }
};

const getRiskBucket = (score) => {
  if (score === null || score === undefined) return 'muted';
  if (score < 30) return 'low';
  if (score < 70) return 'medium';
  return 'high';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} min ago`;
    }
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const countryFlags = {
  US: '🇺🇸', ZA: '🇿🇦', MX: '🇲🇽', AE: '🇦🇪', GB: '🇬🇧',
  CA: '🇨🇦', AU: '🇦🇺', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵',
  CN: '🇨🇳', IN: '🇮🇳', BR: '🇧🇷', RU: '🇷🇺', KR: '🇰🇷',
  SG: '🇸🇬', HK: '🇭🇰', NG: '🇳🇬', KE: '🇰🇪',
};

// Step icons removed - no longer displayed in list view

export default function ApplicantsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { isTestMode, setTestMode } = useTestMode();
  const searchInputRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const riskDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  // Local search state (for immediate UI feedback)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || null,
    risk_level: searchParams.get('risk_level') || null,
    priority: searchParams.get('priority') || null,
    product_type: searchParams.get('product_type') || null,
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || 'created_at',
    order: searchParams.get('order') || 'desc',
    limit: parseInt(searchParams.get('limit') || '50', 10),
    offset: parseInt(searchParams.get('offset') || '0', 10),
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [lastSelectedId, setLastSelectedId] = useState(null); // For shift+click
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // For keyboard nav in dropdowns
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null });
  const createModalTriggerRef = useRef(null); // For focus return

  // Update filters when debounced search changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearch,
      offset: 0,
    }));
  }, [debouncedSearch]);

  // Auto-enable test mode when redirected from SDK demo with ?test_mode=true
  useEffect(() => {
    if (searchParams.get('test_mode') === 'true' && !isTestMode) {
      setTestMode(true);
    }
  }, [searchParams, isTestMode, setTestMode]);

  // Reset pagination when test mode toggles
  useEffect(() => {
    setFilters(prev => ({ ...prev, offset: 0 }));
    setSelectedIds([]);
  }, [isTestMode]);

  // Merge test mode into API filters
  const effectiveFilters = { ...filters, is_sandbox: isTestMode };

  // Fetch applicants with current filters
  const { data, isLoading, error, refetch, isFetching } = useApplicants(effectiveFilters);

  // Batch review mutation
  const batchReviewMutation = useBatchReviewApplicants();
  const deleteApplicantMutation = useDeleteApplicant();

  // Export mutation
  const exportMutation = useExportApplicants();

  // Sync filters to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.risk_level) params.set('risk_level', filters.risk_level);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.product_type) params.set('product_type', filters.product_type);
    if (filters.search) params.set('search', filters.search);
    if (filters.sort && filters.sort !== 'created_at') params.set('sort', filters.sort);
    if (filters.order && filters.order !== 'desc') params.set('order', filters.order);
    if (filters.offset > 0) params.set('offset', filters.offset.toString());
    if (filters.limit !== 50) params.set('limit', filters.limit.toString());
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Click outside handlers for dropdowns
  const closeDropdowns = useCallback(() => {
    setActiveDropdown(null);
    setHighlightedIndex(-1);
  }, []);
  useClickOutside(statusDropdownRef, closeDropdowns, activeDropdown === 'status');
  useClickOutside(riskDropdownRef, closeDropdowns, activeDropdown === 'risk');
  useClickOutside(priorityDropdownRef, closeDropdowns, activeDropdown === 'priority');
  useClickOutside(productDropdownRef, closeDropdowns, activeDropdown === 'product');

  // Keyboard shortcuts
  useKeyboardShortcut(['meta', 'k'], () => searchInputRef.current?.focus());
  useKeyboardShortcut(['ctrl', 'k'], () => searchInputRef.current?.focus());
  useKeyboardShortcut('Escape', closeDropdowns, { enabled: !!activeDropdown });

  // Update filter helper (defined before handleDropdownKeyDown to avoid hoisting issues)
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0,
    }));
  }, []);

  // Get options for current dropdown
  const getDropdownOptions = useCallback(() => {
    if (activeDropdown === 'status') {
      return [null, ...Object.keys(statusConfig)];
    }
    if (activeDropdown === 'risk') {
      return [null, 'low', 'medium', 'high'];
    }
    if (activeDropdown === 'priority') {
      return [null, 'vip', 'priority', 'standard'];
    }
    if (activeDropdown === 'product') {
      // Product types are customer-defined, so we provide some common defaults
      return [null, 'premium', 'standard', 'basic'];
    }
    return [];
  }, [activeDropdown]);

  // Keyboard navigation for dropdowns
  const handleDropdownKeyDown = useCallback((e) => {
    if (!activeDropdown) return;

    const options = getDropdownOptions();
    const maxIndex = options.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, maxIndex));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          const selectedValue = options[highlightedIndex];
          if (activeDropdown === 'status') {
            updateFilter('status', selectedValue);
          } else if (activeDropdown === 'risk') {
            updateFilter('risk_level', selectedValue);
          } else if (activeDropdown === 'priority') {
            updateFilter('priority', selectedValue);
          } else if (activeDropdown === 'product') {
            updateFilter('product_type', selectedValue);
          }
          closeDropdowns();
        }
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(maxIndex);
        break;
      default:
        break;
    }
  }, [activeDropdown, highlightedIndex, getDropdownOptions, updateFilter, closeDropdowns]);

  // Reset highlighted index when dropdown changes
  useEffect(() => {
    if (activeDropdown) {
      // Find current selected index
      let options;
      let currentValue;
      switch (activeDropdown) {
        case 'status':
          options = [null, ...Object.keys(statusConfig)];
          currentValue = filters.status;
          break;
        case 'risk':
          options = [null, 'low', 'medium', 'high'];
          currentValue = filters.risk_level;
          break;
        case 'priority':
          options = [null, 'vip', 'priority', 'standard'];
          currentValue = filters.priority;
          break;
        case 'product':
          options = [null, 'premium', 'standard', 'basic'];
          currentValue = filters.product_type;
          break;
        default:
          options = [];
          currentValue = null;
      }
      const currentIndex = options.indexOf(currentValue);
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  }, [activeDropdown, filters.status, filters.risk_level, filters.priority, filters.product_type]);

  const applicants = data?.items || [];
  const total = data?.total || 0;
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.ceil(total / filters.limit) || 1;

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      offset: (newPage - 1) * prev.limit,
    }));
  };

  // Sorting handler
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sort: field,
      order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc',
      offset: 0,
    }));
  };

  // Get sort icon for column
  const getSortIcon = (field) => {
    if (filters.sort !== field) return <ArrowUpDown size={12} aria-hidden="true" />;
    return filters.order === 'asc'
      ? <ChevronUp size={12} aria-hidden="true" />
      : <ChevronDown size={12} aria-hidden="true" />;
  };

  // CSV Export handler
  const handleExport = async () => {
    try {
      const exportFilters = {
        ...effectiveFilters,
        ids: selectedIds.length > 0 ? selectedIds : undefined,
      };
      delete exportFilters.limit;
      delete exportFilters.offset;

      await exportMutation.mutateAsync(exportFilters);
      toast.success(
        selectedIds.length > 0
          ? `Exported ${selectedIds.length} selected applicants`
          : 'Exported all applicants matching current filters'
      );
    } catch (err) {
      toast.error(`Failed to export: ${err.message}`, {
        action: {
          label: 'Retry',
          onClick: handleExport,
        },
      });
    }
  };

  // Shift+click range selection
  const handleCheckboxClick = (e, applicantId) => {
    e.stopPropagation();

    if (e.shiftKey && lastSelectedId && lastSelectedId !== applicantId) {
      // Find indices
      const ids = applicants.map(a => a.id);
      const lastIdx = ids.indexOf(lastSelectedId);
      const currentIdx = ids.indexOf(applicantId);

      if (lastIdx !== -1 && currentIdx !== -1) {
        const start = Math.min(lastIdx, currentIdx);
        const end = Math.max(lastIdx, currentIdx);
        const rangeIds = ids.slice(start, end + 1);

        setSelectedIds(prev => {
          const newSet = new Set(prev);
          rangeIds.forEach(id => newSet.add(id));
          return Array.from(newSet);
        });
        return;
      }
    }

    // Regular toggle
    setSelectedIds(prev =>
      prev.includes(applicantId) ? prev.filter(i => i !== applicantId) : [...prev, applicantId]
    );
    setLastSelectedId(applicantId);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
    setLastSelectedId(id);
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev =>
      prev.length === applicants.length ? [] : applicants.map(a => a.id)
    );
  };

  const handleRowClick = (applicant) => {
    navigate(`/applicants/${applicant.id}`);
  };

  const handleBatchApprove = () => {
    setConfirmDialog({
      isOpen: true,
      action: 'approve',
      title: 'Approve Selected Applicants',
      message: `Are you sure you want to approve ${selectedIds.length} applicant${selectedIds.length > 1 ? 's' : ''}? This will mark them as verified.`,
    });
  };

  const handleBatchReject = () => {
    setConfirmDialog({
      isOpen: true,
      action: 'reject',
      title: 'Reject Selected Applicants',
      message: `Are you sure you want to reject ${selectedIds.length} applicant${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`,
    });
  };

  const handleBatchDelete = () => {
    setConfirmDialog({
      isOpen: true,
      action: 'delete',
      title: 'Delete Selected Applicants',
      message: `PERMANENTLY DELETE ${selectedIds.length} applicant${selectedIds.length > 1 ? 's' : ''} and ALL associated data (documents, screening, cases)? This is GDPR Article 17 erasure and cannot be undone.`,
    });
  };

  const executeBatchDelete = async () => {
    const affectedIds = [...selectedIds];
    setConfirmDialog({ isOpen: false, action: null });
    let succeeded = 0;
    let failed = 0;
    const failures = [];
    for (const id of affectedIds) {
      try {
        await deleteApplicantMutation.mutateAsync({
          id,
          reason: 'Bulk admin deletion via dashboard',
        });
        succeeded++;
      } catch (err) {
        failed++;
        failures.push({ id, message: err?.message || 'unknown' });
      }
    }
    if (failed === 0) {
      toast.success(`Permanently deleted ${succeeded} applicant${succeeded > 1 ? 's' : ''}`);
    } else if (succeeded === 0) {
      toast.error(`Failed to delete ${failed} applicant${failed > 1 ? 's' : ''}: ${failures[0]?.message}`);
    } else {
      toast.warning(`Deleted ${succeeded} of ${affectedIds.length}. ${failed} failed (legal hold or AML retention).`);
    }
    setSelectedIds([]);
    refetch();
  };

  const executeBatchAction = async () => {
    const action = confirmDialog.action;
    const affectedIds = [...selectedIds]; // Copy for undo
    setConfirmDialog({ isOpen: false, action: null });

    try {
      const result = await batchReviewMutation.mutateAsync({
        ids: selectedIds,
        decision: action,
        notes: `Batch ${action}d via dashboard`,
      });

      const reverseAction = action === 'approve' ? 'reject' : 'approve';

      if (result.failed === 0) {
        toast.success(
          `Successfully ${action}d ${result.succeeded} applicant${result.succeeded > 1 ? 's' : ''}`,
          {
            duration: 10000,
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  await batchReviewMutation.mutateAsync({
                    ids: affectedIds,
                    decision: reverseAction,
                    notes: `Batch undo: reverted ${action} via dashboard`,
                  });
                  toast.info(`Undone - ${affectedIds.length} applicant${affectedIds.length > 1 ? 's' : ''} reverted`);
                } catch (undoErr) {
                  toast.error(`Failed to undo: ${undoErr.message}`);
                }
              },
            },
          }
        );
      } else {
        toast.warning(`${action === 'approve' ? 'Approved' : 'Rejected'} ${result.succeeded} of ${result.total} applicants. ${result.failed} failed.`);
      }

      setSelectedIds([]);
    } catch (err) {
      toast.error(`Failed to ${action} applicants: ${err.message}`, {
        action: {
          label: 'Retry',
          onClick: () => {
            setSelectedIds(affectedIds);
            setConfirmDialog({
              isOpen: true,
              action,
              title: action === 'approve' ? 'Approve Selected Applicants' : 'Reject Selected Applicants',
              message: `Retry: ${action} ${affectedIds.length} applicant${affectedIds.length > 1 ? 's' : ''}?`,
            });
          },
        },
      });
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    toast.success('Applicant created successfully');
    refetch();
  };

  if (error) {
    return (
      <ErrorState
        title="Failed to load applicants"
        message="We couldn't load the applicants list. Please check your connection and try again."
        error={error}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="h-full" data-testid="page-applicants" data-page-ready={!isLoading} data-page-loading={isLoading}>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-[28px] font-semibold tracking-tight text-grey-900 dark:text-grey-50">
            Applicants
          </h1>
          <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
            {isTestMode
              ? 'Viewing sandbox test applicants — these are not real verifications'
              : 'Manage and review individual KYC applications'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 lg:gap-3">
          <Button
            variant="secondary"
            aria-label="Export applicants to CSV"
            onClick={handleExport}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
              <Download size={16} aria-hidden="true" />
            )}
            <span className="hidden sm:inline">
              {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
            </span>
          </Button>
          <Button
            className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:opacity-90"
            aria-label="Run AI batch review on applicants"
            onClick={() => {
              if (selectedIds.length === 0) {
                toast.warning('Select applicants first to run AI batch review');
                return;
              }
              navigate(`/applicants?batch_review=true&ids=${selectedIds.join(',')}`);
              toast.info(`AI Batch Review initiated for ${selectedIds.length} applicant${selectedIds.length > 1 ? 's' : ''}`);
            }}
          >
            <Sparkles size={16} />
            <span className="hidden sm:inline">
              AI Batch Review {selectedIds.length > 0 && `(${selectedIds.length})`}
            </span>
          </Button>
          <Button
            ref={createModalTriggerRef}
            onClick={() => setShowCreateModal(true)}
            aria-label="Create new applicant"
          >
            <Plus size={16} />
            Create Applicant
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 lg:gap-3 p-4 lg:px-5 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-t-xl">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs order-first lg:order-none w-full lg:w-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-grey-400" aria-hidden="true" />
          <Input
            ref={searchInputRef}
            type="text"
            className="pl-10 pr-12"
            placeholder="Search by name, ID, email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search applicants"
            data-testid="search-input"
            data-field="search"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-grey-400 bg-grey-100 dark:bg-grey-800 px-1.5 py-0.5 rounded border border-grey-200 dark:border-grey-700 hidden sm:block" aria-hidden="true">
            ⌘K
          </span>
        </div>

        <div className="hidden lg:block w-px h-6 bg-grey-200 dark:bg-grey-700 mx-1" />

        {/* Status Filter */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
              'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700',
              'text-grey-600 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-700',
              filters.status && 'bg-teal-50 dark:bg-teal-950/30 border-teal-500 text-teal-600 dark:text-teal-400'
            )}
            onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
            onKeyDown={handleDropdownKeyDown}
            aria-expanded={activeDropdown === 'status'}
            aria-haspopup="listbox"
            aria-label="Filter by review status. Use arrow keys to navigate."
            data-testid="status-filter"
            data-filter="status"
            data-value={filters.status || 'all'}
          >
            <Filter size={14} aria-hidden="true" />
            <span className="hidden sm:inline">
              {filters.status ? statusConfig[filters.status]?.label || filters.status : 'Status'}
            </span>
            {filters.status && (
              <X
                size={14}
                onClick={(e) => { e.stopPropagation(); updateFilter('status', null); }}
                className="ml-1 hover:text-red-500"
                aria-label="Clear status filter"
              />
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {activeDropdown === 'status' && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-lg shadow-lg p-2 min-w-[180px] z-50" role="listbox" aria-label="Status options" tabIndex={0}>
              <div
                id="status-option-0"
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                  'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                  !filters.status && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                  highlightedIndex === 0 && 'ring-2 ring-teal-500 ring-inset'
                )}
                onClick={() => { updateFilter('status', null); closeDropdowns(); }}
                role="option"
                aria-selected={!filters.status}
              >
                All Statuses
              </div>
              {Object.entries(statusConfig).map(([key, config], index) => (
                <div
                  key={key}
                  id={`status-option-${index + 1}`}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                    'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                    filters.status === key && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                    highlightedIndex === index + 1 && 'ring-2 ring-teal-500 ring-inset'
                  )}
                  onClick={() => { updateFilter('status', key); closeDropdowns(); }}
                  role="option"
                  aria-selected={filters.status === key}
                >
                  <config.icon size={14} aria-hidden="true" />
                  {config.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Risk Level Filter */}
        <div className="relative" ref={riskDropdownRef}>
          <button
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
              'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700',
              'text-grey-600 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-700',
              filters.risk_level && 'bg-teal-50 dark:bg-teal-950/30 border-teal-500 text-teal-600 dark:text-teal-400'
            )}
            onClick={() => setActiveDropdown(activeDropdown === 'risk' ? null : 'risk')}
            onKeyDown={handleDropdownKeyDown}
            aria-expanded={activeDropdown === 'risk'}
            aria-haspopup="listbox"
            aria-label="Filter by risk level"
          >
            <Shield size={14} aria-hidden="true" />
            <span className="hidden sm:inline">
              {filters.risk_level ? `${filters.risk_level.charAt(0).toUpperCase() + filters.risk_level.slice(1)} Risk` : 'Risk'}
            </span>
            {filters.risk_level && (
              <X
                size={14}
                onClick={(e) => { e.stopPropagation(); updateFilter('risk_level', null); }}
                className="ml-1 hover:text-red-500"
                aria-label="Clear risk filter"
              />
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {activeDropdown === 'risk' && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-lg shadow-lg p-2 min-w-[180px] z-50" role="listbox" tabIndex={0}>
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                  'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                  !filters.risk_level && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                  highlightedIndex === 0 && 'ring-2 ring-teal-500 ring-inset'
                )}
                onClick={() => { updateFilter('risk_level', null); closeDropdowns(); }}
                role="option"
                aria-selected={!filters.risk_level}
              >
                All Risk Levels
              </div>
              {['low', 'medium', 'high'].map((level, index) => (
                <div
                  key={level}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                    'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                    filters.risk_level === level && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                    highlightedIndex === index + 1 && 'ring-2 ring-teal-500 ring-inset'
                  )}
                  onClick={() => { updateFilter('risk_level', level); closeDropdowns(); }}
                  role="option"
                  aria-selected={filters.risk_level === level}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)} Risk
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Priority Filter */}
        <div className="relative" ref={priorityDropdownRef}>
          <button
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
              'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700',
              'text-grey-600 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-700',
              filters.priority && 'bg-teal-50 dark:bg-teal-950/30 border-teal-500 text-teal-600 dark:text-teal-400'
            )}
            onClick={() => setActiveDropdown(activeDropdown === 'priority' ? null : 'priority')}
            onKeyDown={handleDropdownKeyDown}
            aria-expanded={activeDropdown === 'priority'}
            aria-haspopup="listbox"
            aria-label="Filter by priority"
          >
            <Star size={14} aria-hidden="true" />
            <span className="hidden sm:inline">
              {filters.priority ? priorityConfig[filters.priority]?.label || filters.priority : 'Priority'}
            </span>
            {filters.priority && (
              <X
                size={14}
                onClick={(e) => { e.stopPropagation(); updateFilter('priority', null); }}
                className="ml-1 hover:text-red-500"
                aria-label="Clear priority filter"
              />
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {activeDropdown === 'priority' && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-lg shadow-lg p-2 min-w-[180px] z-50" role="listbox" tabIndex={0}>
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                  'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                  !filters.priority && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                  highlightedIndex === 0 && 'ring-2 ring-teal-500 ring-inset'
                )}
                onClick={() => { updateFilter('priority', null); closeDropdowns(); }}
                role="option"
                aria-selected={!filters.priority}
              >
                All Priorities
              </div>
              {['vip', 'priority', 'standard'].map((level, index) => {
                const config = priorityConfig[level];
                const Icon = config?.icon;
                return (
                  <div
                    key={level}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                      'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                      filters.priority === level && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                      highlightedIndex === index + 1 && 'ring-2 ring-teal-500 ring-inset'
                    )}
                    onClick={() => { updateFilter('priority', level); closeDropdowns(); }}
                    role="option"
                    aria-selected={filters.priority === level}
                  >
                    {Icon && <Icon size={14} aria-hidden="true" />}
                    {config?.label}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Product Type Filter */}
        <div className="relative" ref={productDropdownRef}>
          <button
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
              'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700',
              'text-grey-600 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-700',
              filters.product_type && 'bg-teal-50 dark:bg-teal-950/30 border-teal-500 text-teal-600 dark:text-teal-400'
            )}
            onClick={() => setActiveDropdown(activeDropdown === 'product' ? null : 'product')}
            onKeyDown={handleDropdownKeyDown}
            aria-expanded={activeDropdown === 'product'}
            aria-haspopup="listbox"
            aria-label="Filter by product type"
          >
            <Package size={14} aria-hidden="true" />
            <span className="hidden sm:inline">
              {filters.product_type ? filters.product_type.charAt(0).toUpperCase() + filters.product_type.slice(1) : 'Product'}
            </span>
            {filters.product_type && (
              <X
                size={14}
                onClick={(e) => { e.stopPropagation(); updateFilter('product_type', null); }}
                className="ml-1 hover:text-red-500"
                aria-label="Clear product type filter"
              />
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {activeDropdown === 'product' && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-lg shadow-lg p-2 min-w-[180px] z-50" role="listbox" tabIndex={0}>
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                  'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                  !filters.product_type && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                  highlightedIndex === 0 && 'ring-2 ring-teal-500 ring-inset'
                )}
                onClick={() => { updateFilter('product_type', null); closeDropdowns(); }}
                role="option"
                aria-selected={!filters.product_type}
              >
                All Products
              </div>
              {['premium', 'standard', 'basic'].map((type, index) => (
                <div
                  key={type}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer',
                    'text-grey-700 dark:text-grey-300 hover:bg-grey-100 dark:hover:bg-grey-800',
                    filters.product_type === type && 'bg-teal-50 dark:bg-teal-950/30 text-teal-600 dark:text-teal-400',
                    highlightedIndex === index + 1 && 'ring-2 ring-teal-500 ring-inset'
                  )}
                  onClick={() => { updateFilter('product_type', type); closeDropdowns(); }}
                  role="option"
                  aria-selected={filters.product_type === type}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Refresh button */}
        <div className="flex items-center gap-2 ml-auto">
          {isFetching && !isLoading && (
            <RefreshCw size={16} className="animate-spin text-grey-400" aria-label="Loading" />
          )}
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            aria-label="Refresh applicants list"
          >
            <RefreshCw size={14} aria-hidden="true" />
          </IconButton>
        </div>
      </div>

      {isLoading ? (
        <ApplicantsTableSkeleton rows={10} />
      ) : (
        <div className="bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 border-t-0 rounded-b-xl overflow-hidden" data-testid="applicants-list" data-total={applicants.length} data-empty={applicants.length === 0}>
          {applicants.length === 0 ? (
            <div className="py-16 px-10 text-center text-grey-500 dark:text-grey-400">
              <Users size={48} className="mx-auto mb-4 opacity-50" aria-hidden="true" />
              <h3 className="text-grey-900 dark:text-grey-50 font-medium mb-2">No applicants found</h3>
              <p className="text-sm">
                {filters.search || filters.status || filters.risk_level
                  ? 'Try adjusting your filters or search query.'
                  : 'Create your first applicant to get started.'}
              </p>
              {!filters.search && !filters.status && !filters.risk_level && (
                <Button
                  className="mt-4"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus size={16} />
                  Create Applicant
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-12">
                      <div
                        className={cn(
                          'w-[18px] h-[18px] border-2 rounded flex items-center justify-center cursor-pointer transition-colors',
                          'border-grey-300 dark:border-grey-600 hover:border-teal-500',
                          selectedIds.length === applicants.length && 'bg-teal-500 border-teal-500'
                        )}
                        onClick={toggleSelectAll}
                        role="checkbox"
                        aria-checked={selectedIds.length === applicants.length}
                        aria-label="Select all applicants"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggleSelectAll()}
                      >
                        {selectedIds.length === applicants.length && <CheckCircle2 size={12} className="text-white" aria-hidden="true" />}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-grey-900 dark:hover:text-grey-50"
                      onClick={() => handleSort('last_name')}
                      aria-sort={filters.sort === 'last_name' ? filters.order + 'ending' : 'none'}
                    >
                      <div className="flex items-center gap-1">
                        Applicant
                        {getSortIcon('last_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-grey-900 dark:hover:text-grey-50 hidden xl:table-cell"
                      onClick={() => handleSort('priority')}
                      aria-sort={filters.sort === 'priority' ? filters.order + 'ending' : 'none'}
                    >
                      <div className="flex items-center gap-1">
                        Priority
                        {getSortIcon('priority')}
                      </div>
                    </TableHead>
                    <TableHead className="hidden xl:table-cell">Product</TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-grey-900 dark:hover:text-grey-50"
                      onClick={() => handleSort('status')}
                      aria-sort={filters.sort === 'status' ? filters.order + 'ending' : 'none'}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">Screening</TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-grey-900 dark:hover:text-grey-50 hidden lg:table-cell"
                      onClick={() => handleSort('risk_score')}
                      aria-sort={filters.sort === 'risk_score' ? filters.order + 'ending' : 'none'}
                    >
                      <div className="flex items-center gap-1">
                        Risk
                        {getSortIcon('risk_score')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:text-grey-900 dark:hover:text-grey-50 hidden xl:table-cell"
                      onClick={() => handleSort('created_at')}
                      aria-sort={filters.sort === 'created_at' ? filters.order + 'ending' : 'none'}
                    >
                      <div className="flex items-center gap-1">
                        Submitted
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead className="hidden xl:table-cell">Reviewer</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applicants.map((applicant) => {
                    const status = statusConfig[applicant.status] || statusConfig.pending;
                    const StatusIcon = status?.icon;
                    const riskBucket = applicant.risk_level || getRiskBucket(applicant.risk_score);
                    const riskColor = getRiskColor(riskBucket);
                    const countryCode = applicant.country_code || applicant.nationality?.slice(0, 2)?.toUpperCase();

                    const displayName = applicant.first_name && applicant.last_name
                      ? `${applicant.first_name} ${applicant.last_name}`
                      : applicant.email?.split('@')[0] || `Applicant ${applicant.id.slice(0, 8)}`;

                    const flags = applicant.flags || [];

                    return (
                      <TableRow
                        key={applicant.id}
                        className={cn(
                          'cursor-pointer',
                          selectedIds.includes(applicant.id) && 'bg-teal-50/50 dark:bg-teal-950/20'
                        )}
                        onClick={() => handleRowClick(applicant)}
                        data-testid={`applicant-row-${applicant.id}`}
                        data-entity-id={applicant.id}
                        data-status={applicant.status}
                        data-risk-level={getRiskBucket(applicant.risk_score)}
                      >
                        <TableCell>
                          <div
                            className={cn(
                              'w-[18px] h-[18px] border-2 rounded flex items-center justify-center cursor-pointer transition-colors',
                              'border-grey-300 dark:border-grey-600 hover:border-teal-500',
                              selectedIds.includes(applicant.id) && 'bg-teal-500 border-teal-500'
                            )}
                            onClick={(e) => handleCheckboxClick(e, applicant.id)}
                            role="checkbox"
                            aria-checked={selectedIds.includes(applicant.id)}
                            aria-label={`Select ${displayName}. Hold Shift to select range.`}
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && toggleSelect(applicant.id)}
                          >
                            {selectedIds.includes(applicant.id) && <CheckCircle2 size={12} className="text-white" aria-hidden="true" />}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <Avatar size="sm" style={{ background: getAvatarColor(applicant.id) }}>
                              <AvatarFallback className="text-white text-xs font-semibold" style={{ background: getAvatarColor(applicant.id) }}>
                                {getInitials(applicant.first_name, applicant.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <span className="flex items-center gap-1.5 font-medium text-grey-900 dark:text-grey-50 truncate">
                                {displayName}
                                {applicant.is_sandbox && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30" aria-label="Test applicant">
                                    TEST
                                  </span>
                                )}
                              </span>
                              <span className="block text-[11px] text-grey-500 font-mono mt-0.5">
                                {shortRef(applicant.external_id || applicant.id)}
                              </span>
                              <div className="hidden sm:flex items-center gap-2 text-xs text-grey-500 mt-1">
                                {countryCode && (
                                  <>
                                    <span>{countryFlags[countryCode] || '🌍'} {applicant.nationality || countryCode}</span>
                                    <span>•</span>
                                  </>
                                )}
                                <span>{applicant.platform || 'Web'}</span>
                                {applicant.company_name && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-0.5">
                                      <Building2 size={12} aria-hidden="true" />
                                      {applicant.company_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {applicant.priority && applicant.priority !== 'standard' ? (
                            (() => {
                              const pConfig = priorityConfig[applicant.priority];
                              const PriorityIcon = pConfig?.icon;
                              return (
                                <Badge variant={pConfig?.variant || 'muted'} className="gap-1">
                                  {PriorityIcon && <PriorityIcon size={12} aria-hidden="true" />}
                                  {pConfig?.label}
                                </Badge>
                              );
                            })()
                          ) : (
                            <span className="text-grey-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {applicant.product_type ? (
                            <Badge variant="secondary">
                              {applicant.product_type.charAt(0).toUpperCase() + applicant.product_type.slice(1)}
                            </Badge>
                          ) : (
                            <span className="text-grey-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status?.variant || 'init'} className="gap-1.5">
                            {StatusIcon && <StatusIcon size={12} aria-hidden="true" />}
                            {status?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1.5">
                            {flags.length === 0 ? (
                              <Badge variant="flag-clear" className="gap-1 opacity-70">
                                <CheckCircle2 size={10} aria-hidden="true" />
                                Clear
                              </Badge>
                            ) : (
                              (() => {
                                const flagCounts = {};
                                flags.forEach(flag => {
                                  const flagKey = typeof flag === 'string' ? flag : flag.type;
                                  flagCounts[flagKey] = (flagCounts[flagKey] || 0) + 1;
                                });
                                return Object.entries(flagCounts).slice(0, 3).map(([flagKey, count], idx) => {
                                  const flagInfo = screeningConfig[flagKey] || {
                                    shortLabel: flagKey.slice(0, 3).toUpperCase(),
                                    label: formatEnumLabel(flagKey),
                                    variant: 'flag-info'
                                  };
                                  return (
                                    <Badge
                                      key={idx}
                                      variant={flagInfo.variant || 'flag-info'}
                                      title={`${flagInfo.label}: ${count} hit${count > 1 ? 's' : ''}`}
                                    >
                                      {flagInfo.shortLabel || flagInfo.label}
                                      {count > 1 && `:${count}`}
                                    </Badge>
                                  );
                                });
                              })()
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              'font-semibold text-sm',
                              riskColor === 'success' && 'text-green-600',
                              riskColor === 'warning' && 'text-amber-600',
                              riskColor === 'danger' && 'text-red-600',
                              riskColor === 'muted' && 'text-grey-400 text-xs'
                            )}>
                              {applicant.risk_score ?? 'N/A'}
                            </span>
                            {applicant.risk_score !== null && applicant.risk_score !== undefined && (
                              <div className="w-10 h-1 bg-grey-200 dark:bg-grey-700 rounded-full overflow-hidden" aria-hidden="true">
                                <div
                                  className={cn(
                                    'h-full rounded-full',
                                    riskColor === 'success' && 'bg-green-500',
                                    riskColor === 'warning' && 'bg-amber-500',
                                    riskColor === 'danger' && 'bg-red-500'
                                  )}
                                  style={{ width: `${applicant.risk_score}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <span className="text-sm text-grey-500">
                            {formatDate(applicant.submitted_at || applicant.created_at)}
                          </span>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <span className={cn(
                            'text-sm text-grey-500',
                            !applicant.reviewed_by && 'opacity-60 italic'
                          )}>
                            {applicant.reviewed_by || 'Unassigned'}
                          </span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <IconButton
                              variant="ghost"
                              size="sm"
                              title="View Details"
                              aria-label={`View details for ${displayName}`}
                              onClick={() => handleRowClick(applicant)}
                            >
                              <Eye size={16} aria-hidden="true" />
                            </IconButton>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <IconButton
                                  variant="ghost"
                                  size="sm"
                                  title="More Actions"
                                  aria-label={`More actions for ${displayName}`}
                                >
                                  <MoreHorizontal size={16} aria-hidden="true" />
                                </IconButton>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem leftIcon={<Eye size={14} />} onClick={() => navigate(`/applicants/${applicant.id}`)}>
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem leftIcon={<Download size={14} />} onClick={handleExport}>
                                  Export PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem leftIcon={<Shield size={14} />} onClick={() => navigate(`/screening?applicant_id=${applicant.id}`)}>
                                  Run Screening
                                </DropdownMenuItem>
                                <DropdownMenuItem leftIcon={<FolderKanban size={14} />} onClick={() => navigate(`/cases?create=true&applicant_id=${applicant.id}`)}>
                                  Create Case
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  leftIcon={<XCircle size={14} />}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30"
                                  onClick={() => {
                                    setSelectedIds([applicant.id]);
                                    handleBatchReject();
                                  }}
                                >
                                  Reject
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-5 py-4 border-t border-grey-200 dark:border-grey-800">
                <span className="text-sm text-grey-500 dark:text-grey-400">
                  Showing {filters.offset + 1}-{Math.min(filters.offset + filters.limit, total)} of {total} applicants
                </span>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  showFirstLast={false}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Batch Actions Bar */}
      {selectedIds.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-xl shadow-lg z-50"
          role="toolbar"
          aria-label="Batch actions"
        >
          <span className="text-sm font-medium text-grey-900 dark:text-grey-50">
            {selectedIds.length} selected
          </span>
          <div className="w-px h-6 bg-grey-200 dark:bg-grey-700" />
          <Button
            variant="success"
            size="sm"
            onClick={handleBatchApprove}
            disabled={batchReviewMutation.isPending}
            aria-label={`Approve ${selectedIds.length} selected applicants`}
          >
            {batchReviewMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <CheckCircle2 size={14} aria-hidden="true" />
            )}
            <span className="hidden sm:inline">Approve</span>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBatchReject}
            disabled={batchReviewMutation.isPending}
            aria-label={`Reject ${selectedIds.length} selected applicants`}
          >
            {batchReviewMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <XCircle size={14} aria-hidden="true" />
            )}
            <span className="hidden sm:inline">Reject</span>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBatchDelete}
            disabled={deleteApplicantMutation.isPending}
            aria-label={`Permanently delete ${selectedIds.length} selected applicants`}
          >
            {deleteApplicantMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 size={14} aria-hidden="true" />
            )}
            <span className="hidden sm:inline">Delete</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSelectedIds([])}
            aria-label="Clear selection"
          >
            <X size={14} aria-hidden="true" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        </div>
      )}

      {showCreateModal && (
        <CreateApplicantModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          triggerRef={createModalTriggerRef}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={
          confirmDialog.action === 'approve' ? 'Approve All' :
          confirmDialog.action === 'delete' ? 'Permanently Delete' :
          'Reject All'
        }
        variant={confirmDialog.action === 'approve' ? 'success' : 'danger'}
        isLoading={batchReviewMutation.isPending || deleteApplicantMutation.isPending}
        onConfirm={confirmDialog.action === 'delete' ? executeBatchDelete : executeBatchAction}
        onCancel={() => setConfirmDialog({ isOpen: false, action: null })}
      />
    </div>
  );
}
